import { useState, type FormEvent } from "react";
import { browserSupportsWebAuthn, startAuthentication } from "@simplewebauthn/browser";
import type { PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import type { SessaoAtual } from "@noesis/shared";
import { Grifo } from "../componentes/Marca.js";
import { FalhaDaApi, pedir } from "../api.js";

/**
 * Entrada no Noesis. Não existe rota de registro: a única conta nasce do seed.
 * Por isso a tela não oferece "criar conta" — oferecer levaria a um beco.
 *
 * **Dois caminhos, e o de senha nunca some** (RN-36). A passkey aparece em cima
 * porque é o caminho de todo dia no celular; o formulário continua embaixo,
 * inteiro, porque é ele que funciona no aparelho que ainda não foi cadastrado,
 * no navegador que não faz WebAuthn, e no dia em que o telefone se perdeu.
 */
export function Entrar({ aoEntrar }: { aoEntrar: (sessao: SessaoAtual) => void }) {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [comChave, setComChave] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const suportaPasskey = browserSupportsWebAuthn();

  async function entrarComPasskey() {
    if (comChave) return;
    setComChave(true);
    setErro(null);
    try {
      const { desafioId, opcoes } = await pedir("sessao.passkeyOpcoes");
      const resposta = await startAuthentication({
        optionsJSON: opcoes as unknown as PublicKeyCredentialRequestOptionsJSON,
      });
      aoEntrar(await pedir("sessao.passkeyEntrar", { corpo: { desafioId, resposta: resposta as never } }));
    } catch (falha) {
      const nome = falha instanceof Error ? falha.name : "";
      // Cancelar não é falha: é resposta. Nos dois casos a saída é a mesma
      // frase curta e o formulário de senha, que já está logo abaixo.
      setErro(
        nome === "NotAllowedError" || nome === "AbortError"
          ? "Entrada por passkey cancelada. Use a senha, se preferir."
          : falha instanceof FalhaDaApi
            ? falha.message
            : "Não deu para entrar com a passkey. Use a senha.",
      );
      setComChave(false);
    }
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      aoEntrar(await pedir("sessao.entrar", { corpo: { email, senha } }));
    } catch (falha) {
      setErro(falha instanceof FalhaDaApi ? falha.message : "Não foi possível entrar.");
      setEnviando(false);
    }
  }

  return (
    <div className="tela tela--entrada">
      <div className="tela__corpo tela__corpo--centrado">
        <header className="entrada__cabecalho">
          <p className="entrada__marca">
            <Grifo tamanho="hero" />
          </p>
          <h1 className="t-titulo">Bom te ver.</h1>
          <p className="leitura--apoio">
            Prática por recuperação ativa e repetição espaçada. Um lugar só, para o ciclo inteiro.
          </p>
        </header>

        {suportaPasskey && (
          <div className="pilha">
            <button className="botao" type="button" disabled={comChave} onClick={() => void entrarComPasskey()}>
              {comChave ? "Falando com o aparelho…" : "Entrar com passkey"}
            </button>
            <p className="etiqueta tinta-suave">ou entre com a senha</p>
          </div>
        )}

        <form className="pilha" onSubmit={(e) => void enviar(e)} noValidate>
          <div className="campo">
            <label className="campo__rotulo" htmlFor="email">
              E-mail
            </label>
            <input
              id="email"
              className="campo__entrada"
              type="email"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={erro ? true : undefined}
            />
          </div>

          <div className="campo">
            <label className="campo__rotulo" htmlFor="senha">
              Senha
            </label>
            <input
              id="senha"
              className="campo__entrada"
              type="password"
              autoComplete="current-password"
              required
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              aria-invalid={erro ? true : undefined}
            />
          </div>

          {/* Anunciado por leitor de tela no momento em que aparece. */}
          <p className="campo__erro" role="alert" aria-live="polite">
            {erro ?? ""}
          </p>

          <button className="botao" type="submit" disabled={enviando || !email || !senha}>
            {enviando ? "Entrando…" : "Entrar"}
          </button>
        </form>
      </div>
    </div>
  );
}
